/**
 * DEPARTMENTS, CATEGORIES AND DOCUMENT TYPES: AN ADMINISTRATION SURFACE.
 *
 * Three tabs rather than three full lists stacked down one page, because an
 * organization with thirty departments could not see the categories without
 * scrolling past all of them, and one with a hundred could not work at all.
 *
 * WHAT WAS MISSING, AND WHY IT MATTERED. Nothing here could be EDITED. The
 * backend has always taken an id on its upsert and updated in place, so the
 * capability was there and unreached: an administrator correcting a typo, a
 * code or a colour had to delete the item and make a new one, which is a NEW
 * IDENTITY. Every policy, workflow and staff assignment pointing at the old id
 * would have been orphaned by a spelling correction.
 *
 * SO EDIT PRESERVES THE ID, and the row says so. A rename carries onto the
 * policies that display the name, and through them onto search; published
 * versions and approval routing keep what they recorded, because that is what
 * was true then.
 *
 * ARCHIVE IS NOT DELETE AND NOT A CASCADE. An archived department is still on
 * every policy, still assigned to staff, still routing workflows. It stops
 * being offered for new work, and that is all.
 *
 * DELETE IS FOR SOMETHING NOTHING POINTS AT. The counts here are advisory; the
 * callable asks again for itself at the moment of deleting, because a number
 * the browser loaded a minute ago is not a fact about now.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import {
  KINDS,
  TAXONOMY_KINDS,
  useDeleteTaxonomy,
  useItemUsage,
  useSetTaxonomyActive,
  useTaxonomy,
  useTaxonomyUsage,
  useUpsertTaxonomy,
  type TaxonomyItem,
  type TaxonomyKind,
  type TaxonomyUsage,
} from './useTaxonomy';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { TabButtons } from '../../shared/Tabs';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';
import tax from './Taxonomy.module.css';
import form from '../../shared/Form.module.css';
import { TAXONOMY_CODE_MAX, TAXONOMY_NAME_MAX } from '../../types/fieldLimits';

/** The palette the picker offers. Six-digit hex, which the backend requires. */
const COLORS = [
  '#1E88E5', '#00ACC1', '#43A047', '#FB8C00',
  '#E53935', '#8E24AA', '#757575', '#D81B60',
];

/** Only departments and categories carry a separate numbering code. */
const HAS_POLICY_CODE: Record<TaxonomyKind, boolean> = {
  department: true, category: true, documentType: false,
};

type StatusFilter = 'active' | 'archived' | 'all';

function errorText(e: unknown, fallback: string): string {
  if (isCallFailure(e)) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

/** "29 policies · 2 workflows · 8 staff", or a dash, or "not known". */
function usageText(usage: TaxonomyUsage | undefined, kind: TaxonomyKind): string {
  if (!usage) return '';
  const parts: string[] = [];
  const add = (n: number | null, one: string, many: string) => {
    // A count that could not be established is said, never shown as zero.
    if (n === null) { parts.push(`${many} not known`); return; }
    if (n === 0) return;
    parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(usage.policies, 'policy', 'policies');
  if (kind !== 'category') add(usage.workflows, 'workflow', 'workflows');
  if (kind === 'department') add(usage.staff, 'staff member', 'staff');
  return parts.length ? parts.join(' · ') : 'Nothing yet';
}

interface FormValues { name: string; code: string; policyCode: string; color: string }

function emptyValues(): FormValues {
  return { name: '', code: '', policyCode: '', color: COLORS[0]! };
}

function valuesOf(item: TaxonomyItem): FormValues {
  return {
    name: item.name,
    code: item.code ?? '',
    policyCode: item.policyCode ?? '',
    color: item.color ?? COLORS[0]!,
  };
}

/**
 * The add and edit form, which are the same form.
 *
 * An inline panel rather than a modal: it is the pattern the policy record
 * already uses for Edit details, it needs no focus trap to get wrong, and on
 * a row it opens where the person was already looking.
 */
function ItemForm({
  kind, item, busy, error, onSave, onCancel,
}: {
  kind: TaxonomyKind;
  item: TaxonomyItem | null;
  busy: boolean;
  error: unknown;
  onSave: (values: FormValues) => void;
  onCancel: () => void;
}) {
  const config = KINDS[kind];
  const [values, setValues] = useState<FormValues>(item ? valuesOf(item) : emptyValues());
  const [touched, setTouched] = useState(false);
  const nameMissing = touched && !values.name.trim();
  const set = (patch: Partial<FormValues>) => setValues((v) => ({ ...v, ...patch }));

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!values.name.trim()) return;
    onSave(values);
  }

  return (
    <form className={tax.form} onSubmit={submit} aria-label={item ? `Edit ${item.name}` : `Add a ${config.singular}`}>
      {item && (
        <p className={own.hint}>
          {/* The whole point of editing rather than replacing. */}
          Editing keeps this {config.singular}&rsquo;s identity, so every policy, workflow and
          staff assignment that points at it stays pointed at it.
        </p>
      )}

      {error !== null && error !== undefined && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {errorText(error, `That ${config.singular} could not be saved.`)}
        </div>
      )}

      <div className={tax.formRow}>
        <span className={tax.field}>
          <label className={own.settingLabel} htmlFor={`${kind}-name`}>Name</label>
          <input
            id={`${kind}-name`}
            className={own.input}
            value={values.name}
            maxLength={TAXONOMY_NAME_MAX}
            onChange={(e) => set({ name: e.target.value })}
            aria-invalid={nameMissing || undefined}
            aria-describedby={nameMissing ? `${kind}-name-err` : undefined}
            disabled={busy}
            autoFocus
          />
          {nameMissing && <p className={form.error} id={`${kind}-name-err`}>Enter a name.</p>}
        </span>

        <span className={tax.fieldNarrow}>
          <label className={own.settingLabel} htmlFor={`${kind}-code`}>Code</label>
          <input
            id={`${kind}-code`}
            className={own.input}
            value={values.code}
            maxLength={TAXONOMY_CODE_MAX}
            onChange={(e) => set({ code: e.target.value })}
            disabled={busy}
          />
        </span>

        {HAS_POLICY_CODE[kind] && (
          <span className={tax.fieldNarrow}>
            <label className={own.settingLabel} htmlFor={`${kind}-policy-code`}>Policy number code</label>
            <input
              id={`${kind}-policy-code`}
              className={own.input}
              value={values.policyCode}
              maxLength={10}
              onChange={(e) => set({ policyCode: e.target.value })}
              aria-describedby={`${kind}-policy-code-help`}
              disabled={busy}
            />
          </span>
        )}
      </div>

      {HAS_POLICY_CODE[kind] && (
        <p className={own.hint} id={`${kind}-policy-code-help`}>
          The short letters this {config.singular} contributes to a new policy&rsquo;s number, such as
          NUR or ADM. Changing it never renumbers policies that already have a number; a policy
          number is a permanent identifier. It applies to numbers issued from now on.
        </p>
      )}

      <fieldset className={tax.colors}>
        <legend className={own.settingLabel}>Color</legend>
        <p className={own.hint}>
          How it is marked on screen, and nothing else. Color never affects routing, numbering,
          who is asked to acknowledge, or who may see anything.
        </p>
        <div className={tax.swatches} role="radiogroup" aria-label="Color">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={values.color === c}
              aria-label={c}
              className={values.color === c ? `${tax.swatchBtn} ${tax.swatchOn}` : tax.swatchBtn}
              style={{ background: c }}
              onClick={() => set({ color: c })}
              disabled={busy}
            />
          ))}
        </div>
      </fieldset>

      <div className={tax.formActions}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
          {busy ? 'Saving…' : item ? 'Save changes' : `Add ${config.singular}`}
        </button>
        <button type="button" className={styles.btn} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

function TaxonomyTab({ kind }: { kind: TaxonomyKind }) {
  const config = KINDS[kind];
  const { session } = useAuth();
  const items = useTaxonomy(kind, Boolean(session));
  const usage = useTaxonomyUsage(kind, Boolean(session));
  const upsert = useUpsertTaxonomy(kind);
  const remove = useDeleteTaxonomy(kind);
  const setActive = useSetTaxonomyActive(kind);
  const itemUsage = useItemUsage(kind);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState<{ id: string; name: string; summary: string; unused: boolean } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const all = items.data ?? [];
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((item) => {
      if (status === 'active' && !item.isActive) return false;
      if (status === 'archived' && item.isActive) return false;
      if (!q) return true;
      // Name and code, which is what somebody types when looking for one.
      return item.name.toLowerCase().includes(q)
        || (item.code ?? '').toLowerCase().includes(q)
        || (item.policyCode ?? '').toLowerCase().includes(q);
    });
  }, [all, query, status, kind]);

  const archivedCount = all.filter((i) => !i.isActive).length;
  const busy = upsert.isPending || remove.isPending || setActive.isPending;

  function save(values: FormValues, id: string | null) {
    upsert.mutate(
      {
        ...(id ? { [config.idField]: id } : {}),
        name: values.name.trim(),
        code: values.code.trim() || undefined,
        ...(HAS_POLICY_CODE[kind] ? { policyCode: values.policyCode.trim() || undefined } : {}),
        color: values.color,
      },
      {
        onSuccess: (result) => {
          setEditing(null);
          setAdding(false);
          const updated = (result as { policiesUpdated?: number } | undefined)?.policiesUpdated;
          setNotice(
            id
              ? updated
                ? `Saved. ${updated} ${updated === 1 ? 'policy' : 'policies'} now show the new name, and search will follow.`
                : 'Saved.'
              : `${values.name.trim()} added.`,
          );
        },
      },
    );
  }

  async function askBeforeRemoving(item: TaxonomyItem) {
    setNotice(null);
    // Read the counts now rather than trusting what the list loaded.
    const fresh = await itemUsage.mutateAsync(item.id).catch(() => null);
    setConfirming({
      id: item.id,
      name: item.name,
      summary: fresh?.summary ?? 'an unknown number of records',
      unused: fresh?.unused ?? false,
    });
  }

  return (
    <>
      <p className={own.hint} style={{ maxWidth: '70ch' }}>{config.hint}</p>

      {(remove.error || setActive.error) && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {errorText(remove.error ?? setActive.error, `That ${config.singular} could not be changed.`)}
        </div>
      )}
      {notice && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{notice}</div>}

      <div className={tax.toolbar}>
        <span className={tax.search}>
          <label className={own.settingLabel} htmlFor={`${kind}-search`}>Search</label>
          <input
            id={`${kind}-search`}
            type="search"
            className={own.input}
            value={query}
            maxLength={TAXONOMY_NAME_MAX}
            onChange={(e) => setQuery(e.target.value)}
          />
        </span>

        <span className={tax.filters} role="group" aria-label="Show">
          {(['active', 'archived', 'all'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              className={status === s ? `${tax.filter} ${tax.filterOn}` : tax.filter}
              onClick={() => setStatus(s)}
            >
              {s === 'active' ? 'Active' : s === 'archived' ? `Archived${archivedCount ? ` (${archivedCount})` : ''}` : 'All'}
            </button>
          ))}
        </span>

        {/* Hidden while the add form is open: the form IS the add, and two
            buttons with the same words is an ambiguity for a screen reader as
            much as for a person. */}
        {!adding && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => { setAdding(true); setEditing(null); setNotice(null); }}
            disabled={busy}
          >
            Add {config.singular}
          </button>
        )}
      </div>

      <p className={own.hint}>
        {shown.length} of {all.length} {all.length === 1 ? config.singular : config.label.toLowerCase()}
        {usage.data && !usage.data.complete && (
          <> · usage counted for the first {usage.data.counted}</>
        )}
      </p>

      {adding && (
        <ItemForm
          kind={kind}
          item={null}
          busy={upsert.isPending}
          error={upsert.error}
          onSave={(v) => save(v, null)}
          onCancel={() => { setAdding(false); upsert.reset(); }}
        />
      )}

      {items.isLoading ? (
        <Spinner label={`Loading ${config.label.toLowerCase()}`} />
      ) : items.error ? (
        // A failed read, said as one. It used to fall through to "No
        // departments yet" under a banner saying something could not be changed.
        <PageState title={`Could not load ${config.label.toLowerCase()}`} tone="alert">
          {errorText(items.error, 'The list did not come back. Reload the page to try again.')}
        </PageState>
      ) : all.length === 0 ? (
        <PageState title={`No ${config.label.toLowerCase()} yet`}>
          Policies need somewhere to live before they can be filed. Add the first one above.
        </PageState>
      ) : shown.length === 0 ? (
        <p className={own.hint}>Nothing matches.</p>
      ) : (
        <div className={tax.tableWrap}>
          <table className={tax.table}>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Code</th>
                <th scope="col">Color</th>
                <th scope="col">Used by</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="vh">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((item) => (
                <tr key={item.id}>
                  <td colSpan={editing === item.id ? 6 : undefined}>
                    {editing === item.id ? (
                      <ItemForm
                        kind={kind}
                        item={item}
                        busy={upsert.isPending}
                        error={upsert.error}
                        onSave={(v) => save(v, item.id)}
                        onCancel={() => { setEditing(null); upsert.reset(); }}
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  {editing === item.id ? null : (
                    <>
                      <td className="tnum">{item.code ?? item.policyCode ?? <span className={styles.absent}>&mdash;</span>}</td>
                      <td>
                        {item.color ? (
                          <span className={tax.swatch} style={{ background: item.color }} aria-label={item.color} role="img" />
                        ) : (
                          <span className={styles.absent}>&mdash;</span>
                        )}
                      </td>
                      <td className={own.hint}>
                        {usage.isLoading ? 'Counting…' : usageText(usage.data?.usage[item.id], kind)}
                      </td>
                      <td>
                        {item.isActive
                          ? <span className={`${styles.tag} ${styles.ok}`}>Active</span>
                          : <span className={`${styles.tag} ${styles.neutral}`}>Archived</span>}
                      </td>
                      <td className={tax.actions}>
                        <button type="button" className={styles.btn} disabled={busy}
                          onClick={() => { setEditing(item.id); setAdding(false); setNotice(null); upsert.reset(); }}>
                          Edit
                        </button>
                        {item.isActive ? (
                          <button type="button" className={styles.btn} disabled={busy}
                            onClick={() => setActive.mutate({ id: item.id, isActive: false }, {
                              onSuccess: (r) => setNotice(
                                `${item.name} archived. It is no longer offered for new work; `
                                + `${(r as { stillUsedBy?: string })?.stillUsedBy ?? 'existing records'} still refer to it.`,
                              ),
                            })}>
                            Archive
                          </button>
                        ) : (
                          <button type="button" className={styles.btn} disabled={busy}
                            onClick={() => setActive.mutate({ id: item.id, isActive: true }, {
                              onSuccess: () => setNotice(`${item.name} is active again.`),
                            })}>
                            Reactivate
                          </button>
                        )}
                        <button type="button" className={styles.btn} disabled={busy}
                          onClick={() => { void askBeforeRemoving(item); }}>
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <div className={`${styles.notice} ${confirming.unused ? styles.noticeWarn : styles.noticeDanger}`} role="alertdialog" aria-label={`Delete ${confirming.name}`}>
          {confirming.unused ? (
            <>
              <p>
                <strong>Delete &ldquo;{confirming.name}&rdquo;?</strong> Nothing refers to it, so it can go.
                This cannot be undone.
              </p>
              <div className={tax.formActions}>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                  onClick={() => remove.mutate({ [config.idField]: confirming.id }, {
                    onSuccess: () => { setNotice(`${confirming.name} deleted.`); setConfirming(null); },
                  })}>
                  Delete it
                </button>
                <button type="button" className={styles.btn} onClick={() => setConfirming(null)}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <p>
                <strong>&ldquo;{confirming.name}&rdquo; is used by {confirming.summary}.</strong> It cannot be
                deleted. Archive it instead: existing records go on referring to it, and it stops being
                offered for new work.
              </p>
              <div className={tax.formActions}>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                  onClick={() => setActive.mutate({ id: confirming.id, isActive: false }, {
                    onSuccess: () => { setNotice(`${confirming.name} archived.`); setConfirming(null); },
                  })}>
                  Archive it
                </button>
                <button type="button" className={styles.btn} onClick={() => setConfirming(null)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export function TaxonomyPage() {
  const [tab, setTab] = useState<TaxonomyKind>('department');

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Taxonomy</h1>
        <p className={styles.subtitle}>
          How your policies are filed. Renaming one of these keeps it the same thing: every policy,
          workflow and staff assignment that points at it stays pointed at it, and the new name
          follows onto the policies that show it.
        </p>
      </header>

      <div className={styles.body}>
        <TabButtons
          label="Taxonomy"
          items={TAXONOMY_KINDS.map((kind) => ({ key: kind, label: KINDS[kind].label }))}
          current={tab}
          onSelect={setTab}
        />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{KINDS[tab].label}</h2>
          <TaxonomyTab key={tab} kind={tab} />
        </section>
      </div>
    </>
  );
}
